import { useState } from 'react';
import { Zap, Copy, Check, AlertCircle } from 'lucide-react';
import { jsonToTypeScript } from '../utils/jsonToTypeScript';
import { jsonToZod } from '../utils/jsonToZod';
import { jsonToToon } from '../utils/toonConverter';

export default function QuickConvertWidget() {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [outputType, setOutputType] = useState<'typescript' | 'zod' | 'toon'>('typescript');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const convert = () => {
    if (!input.trim()) {
      setError('Please enter some JSON');
      setOutput('');
      return;
    }

    try {
      JSON.parse(input);
      setError('');

      let result = '';
      switch (outputType) {
        case 'typescript':
          result = jsonToTypeScript(input);
          break;
        case 'zod':
          result = jsonToZod(input);
          break;
        case 'toon':
          result = jsonToToon(input);
          break;
      }
      setOutput(result);
    } catch (err) {
      setError('Invalid JSON');
      setOutput('');
    }
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Quick Convert</h3>
          <p className="text-sm text-gray-500">Test conversions instantly</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-3 rounded-xl">
          <Zap className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Output type selector */}
      <div className="flex gap-2 mb-3">
        <button
          onClick={() => setOutputType('typescript')}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
            outputType === 'typescript'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          TypeScript
        </button>
        <button
          onClick={() => setOutputType('zod')}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
            outputType === 'zod'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Zod
        </button>
        <button
          onClick={() => setOutputType('toon')}
          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
            outputType === 'toon'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          TOON
        </button>
      </div>

      {/* Input area */}
      <div className="space-y-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='{"name": "John", "age": 30}'
          className="w-full h-24 p-3 border border-gray-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-coral-500 resize-none"
        />

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <button
          onClick={convert}
          className="w-full bg-gradient-to-r from-coral-500 to-teal-500 text-white py-2.5 rounded-lg font-medium hover:shadow-lg transition-all"
        >
          Convert
        </button>

        {/* Output area */}
        {output && (
          <div className="relative">
            <pre className="w-full h-32 p-3 bg-gray-50 border border-gray-300 rounded-lg font-mono text-xs overflow-auto">
              {output}
            </pre>
            <button
              onClick={copyOutput}
              className="absolute top-2 right-2 p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
